import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { getOccupationGroupContent } from "@/lib/occupationContent";

interface StaticOccupationContentProps {
  occupationTitle: string;
  locationName: string;
  socCode?: string;
}

/**
 * Static SEO content component that doesn't require API data.
 * Renders occupation-family-specific editorial content (keyed off the SOC
 * major group) so salary pages carry meaningfully different prose per
 * occupation instead of one interchangeable template.
 */
export default function StaticOccupationContent({ 
  occupationTitle, 
  locationName,
  socCode,
}: StaticOccupationContentProps) {
  const group = getOccupationGroupContent(socCode);

  return (
    <section className="py-6 space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-2xl font-semibold mb-4">
            How {group.groupName} Careers Are Structured
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {group.workContext}
          </p>
          <p className="text-muted-foreground leading-relaxed">
            The wage tables and percentile chart on this page show how {occupationTitle.toLowerCase()}{' '}
            pay is distributed in {locationName} specifically — and because state-level wages for the
            same occupation routinely differ by 30% or more, the local figures here are the ones that
            matter for offers, raises, and relocation decisions in this market.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h2 className="text-2xl font-semibold mb-4">What Moves Pay in This Field</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {group.payDrivers}
          </p>
          <p className="text-muted-foreground leading-relaxed">
            {group.entryPath}
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            New to reading wage distributions? Our guide to{' '}
            <Link to="/insights/how-to-read-salary-percentiles/" className="text-primary hover:underline">
              salary percentiles
            </Link>{' '}
            explains how to place yourself on the chart above, and the{' '}
            <Link to="/insights/salary-negotiation-guide/" className="text-primary hover:underline">
              negotiation playbook
            </Link>{' '}
            shows how to use these numbers in an actual conversation.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
